import React from 'react'
import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  ReferenceInput,
  ArrayInput,
  SimpleFormIterator,
  BooleanInput,
  useRecordContext,
  Labeled,
  TextField,
  DateField,
  TopToolbar,
  Toolbar,
  SaveButton,
  DeleteButton,
  ListButton,
  FormDataConsumer,
  useDataProvider,
  useNotify,
  useRefresh,
} from 'react-admin'
import {
  Grid,
  Box,
  Typography,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Chip,
  Button,
} from '@mui/material'
import {
  InformationCircleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

const UNIVERSAL_FIELDS = [
  'Supplier Name',
  'Signatory Details',
  'Contract Type',
  'Categorisation',
  'Linked Contracts',
  'Parent Agreement',
  'Referenced Schedules',
  'Effective Date',
  'Expiration Date',
  'Auto-Renewal Clause',
  'Notice Period',
  'Payment Terms',
  'Contract Value',
  'Liability Cap',
  'Indemnification',
  'Governing Law',
  'Dispute Resolution',
  'Termination Rights',
  'Change of Control',
  'Data Privacy',
  'Confidentiality',
  'Force Majeure',
  'Insurance',
  'Assignment Rights',
]

const getConfidenceColor = (confidence) => {
  if (confidence >= 80) return '#16a34a'
  if (confidence >= 50) return '#d97706'
  return '#dc2626'
}

const isFieldMandatory = (fieldName) => {
  if (!fieldName) return false
  const normalizedFieldName = fieldName.toLowerCase()
  return UNIVERSAL_FIELDS.some(
    (uf) =>
      normalizedFieldName.includes(uf.toLowerCase()) ||
      uf.toLowerCase().includes(normalizedFieldName)
  )
}

const MetadataFieldLabel = ({ field }) => {
  const isMandatory = isFieldMandatory(field.field_name)

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={0.75}>
        <Typography
          variant="subtitle2"
          component="span"
          sx={{
            fontWeight: 600,
            color: '#1f2937',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {field.field_name}
        </Typography>
        {field.evidence_quote && (
          <Tooltip title={field.evidence_quote} arrow placement="top">
            <IconButton
              size="small"
              sx={{
                color: '#6b7280',
                p: 0.25,
                '&:hover': { backgroundColor: '#f3f4f6' },
              }}
            >
              <InformationCircleIcon className="h-4 w-4 text-stone-400" />
            </IconButton>
          </Tooltip>
        )}
        {field.is_verified && (
          <CheckCircleIcon className="h-4 w-4 text-green-500" />
        )}
      </Box>
      {isMandatory && (
        <Chip
          label="MANDATORY"
          size="small"
          sx={{
            mt: 0.5,
            height: 20,
            fontSize: '0.65rem',
            backgroundColor: '#7c2d12',
            color: '#fff',
            fontWeight: 'bold',
            letterSpacing: '0.04em',
          }}
        />
      )}
    </Box>
  )
}

const ContractSummaryHeader = () => {
  const record = useRecordContext()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const [loading, setLoading] = React.useState(false)
  const [previewLoading, setPreviewLoading] = React.useState(false)

  if (!record) return null

  const handleRetry = () => {
    setLoading(true)
    dataProvider
      .retryExtraction(record.id)
      .then(() => {
        notify('Extraction retried successfully', { type: 'info' })
        refresh()
      })
      .catch((error) => {
        notify(`Error retrying extraction: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handlePreview = () => {
    if (!record?.id || previewLoading) return
    setPreviewLoading(true)
    dataProvider
      .getContractPreviewUrl(record.id)
      .then((res) => {
        if (!res?.url) {
          throw new Error('No preview URL returned')
        }
        window.open(res.url, '_blank', 'noopener,noreferrer')
      })
      .catch((error) => {
        notify(`Error opening preview: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setPreviewLoading(false)
      })
  }

  const statusLabelMap = {
    uploaded: 'UPLOADED',
    processing: 'PROCESSING',
    review_required: 'REVIEW REQUIRED',
    completed: 'VERIFIED',
    failed: 'FAILED',
    extraction_failed: 'EXTRACTION FAILED',
  }

  const stats = {
    total: record.metadata_fields?.length || 0,
    verified: record.metadata_fields?.filter((f) => f.is_verified).length || 0,
    confidence:
      typeof record.ai_confidence === 'number'
        ? Math.round(record.ai_confidence * 100)
        : 0,
  }
  const terminalNoDataFailure =
    typeof record.extraction_error_message === 'string' &&
    (record.extraction_error_message.includes(
      'No usable metadata values extracted from document'
    ) ||
      record.extraction_error_message.includes(
        'Unsupported PDF portfolio format'
      ))
  const canRetry =
    record.status === 'extraction_failed' ||
    (record.status === 'failed' && !terminalNoDataFailure) ||
    (record.status === 'review_required' && stats.total === 0)

  const confidenceColor = getConfidenceColor(stats.confidence)

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 3,
        borderColor: '#e5e7eb',
        borderRadius: 2,
        background:
          'linear-gradient(135deg, rgba(249,250,251,1) 0%, rgba(255,255,255,1) 100%)',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography variant="overline" color="textSecondary">
              Contract Name
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
              {record.filename}
            </Typography>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="overline" color="textSecondary">
              Avg Confidence
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: confidenceColor,
                }}
              />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {stats.confidence}%
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="overline" color="textSecondary">
              Verification
            </Typography>
            <Typography variant="h6">
              {stats.verified} / {stats.total}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography variant="overline" color="textSecondary">
                  Current Status
                </Typography>
                <Box mt={0.5}>
                  <Chip
                    label={statusLabelMap[record.status] || 'UNKNOWN'}
                    color={
                      record.status === 'failed' ||
                      record.status === 'extraction_failed'
                        ? 'error'
                        : record.status === 'review_required'
                        ? 'warning'
                        : record.status === 'completed'
                        ? 'success'
                        : 'default'
                    }
                    variant="filled"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handlePreview}
                  disabled={previewLoading}
                  sx={{
                    minWidth: 96,
                    px: 1.5,
                    whiteSpace: 'nowrap',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {previewLoading ? 'Opening...' : 'Preview'}
                </Button>
                {canRetry && (
                  <Button
                    variant="contained"
                    color={record.status === 'uploaded' ? 'primary' : 'error'}
                    size="small"
                    startIcon={
                      <ArrowPathIcon
                        className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
                      />
                    }
                    onClick={handleRetry}
                    disabled={loading}
                    sx={{
                      minWidth: 96,
                      px: 1.5,
                      whiteSpace: 'nowrap',
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Retry
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

const ContractStatusPolling = () => {
  const record = useRecordContext()
  const refresh = useRefresh()

  React.useEffect(() => {
    if (!record) return undefined

    const inProgressStatuses = ['uploaded', 'processing']
    if (!inProgressStatuses.includes(record.status)) return undefined

    const intervalId = window.setInterval(() => {
      refresh()
    }, 4000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [record, record?.status, refresh])

  return null
}

const ConfidenceValue = ({ record, source }) => {
  const fieldName =
    source && source.includes('.') ? source.split('.').pop() : source
  const score = record && fieldName ? record[fieldName] : 0
  const confidence = Math.round((score || 0) * 100)
  const color = getConfidenceColor(confidence)

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: color,
        }}
      />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {confidence}%
      </Typography>
    </Box>
  )
}

const CreateVendorButton = ({ contractId, supplierName, currentVendorId }) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const [loading, setLoading] = React.useState(false)

  // Don't show if already linked to a vendor
  if (currentVendorId || !supplierName || supplierName === 'None') return null

  const handleCreateVendor = () => {
    setLoading(true)
    dataProvider
      .createVendorFromContract(contractId, supplierName)
      .then((response) => {
        notify(response.message, { type: 'success' })
        refresh()
      })
      .catch((error) => {
        notify(`Error creating vendor: ${error.message}`, { type: 'warning' })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      onClick={handleCreateVendor}
      disabled={loading}
      sx={{ mt: 1 }}
    >
      {loading ? 'Creating...' : 'Create Vendor from This Name'}
    </Button>
  )
}

const MetadataIterator = (props) => {
  return (
    <SimpleFormIterator {...props} disableAdd disableRemove>
      <FormDataConsumer>
        {({ getSource, scopedFormData, formData }) => {
          const fieldValueSource = getSource
            ? getSource('field_value')
            : undefined
          const confidenceSource = getSource
            ? getSource('confidence_score')
            : undefined
          const evidenceSource = getSource
            ? getSource('evidence_quote')
            : undefined
          const verifiedSource = getSource
            ? getSource('is_verified')
            : undefined

          const isMandatory = isFieldMandatory(scopedFormData?.field_name)
          const isSupplierName = scopedFormData?.field_name === 'Supplier Name'

          return (
            <Box
              sx={{
                border: isMandatory ? '1px solid #f59e0b' : '1px solid #e5e7eb',
                borderLeft: isMandatory
                  ? '4px solid #d97706'
                  : '1px solid #e5e7eb',
                borderRadius: 2,
                mb: 2.5,
                px: 2,
                py: 2,
                width: '100%',
                backgroundColor: isMandatory ? '#fffbeb' : '#ffffff',
              }}
            >
              <Grid
                container
                spacing={3}
                alignItems="start"
                sx={{ width: '100%' }}
              >
                <Grid item xs={12} lg={3}>
                  <Labeled
                    label={<MetadataFieldLabel field={scopedFormData} />}
                    sx={{ '& .MuiFormLabel-root': { mb: 1 } }}
                  >
                    <Box sx={{ pt: 0.5 }}>
                      {confidenceSource && (
                        <ConfidenceValue
                          source={confidenceSource}
                          record={scopedFormData}
                        />
                      )}
                    </Box>
                  </Labeled>
                </Grid>
                <Grid item xs={12} lg={7}>
                  {fieldValueSource && (
                    <>
                      <TextInput
                        source={fieldValueSource}
                        label="Extracted Value"
                        multiline
                        fullWidth
                        sx={{
                          '& .MuiInputBase-root': {
                            minHeight: '56px',
                            backgroundColor: '#fff',
                          },
                        }}
                      />
                      {isSupplierName && scopedFormData?.is_verified && (
                        <CreateVendorButton
                          contractId={formData.id}
                          supplierName={scopedFormData?.field_value}
                          currentVendorId={formData.vendor_id}
                        />
                      )}
                      {evidenceSource && (
                        <TextInput
                          source={evidenceSource}
                          label="Source Evidence"
                          multiline
                          fullWidth
                          disabled
                          sx={{
                            mt: 1.5,
                            '& .MuiInputBase-root': {
                              minHeight: '56px',
                              backgroundColor: '#f9fafb',
                            },
                          }}
                        />
                      )}
                    </>
                  )}
                </Grid>
                <Grid item xs={12} lg={2}>
                  <Box
                    sx={{
                      mt: { xs: 0.5, lg: 2.5 },
                      display: 'flex',
                      justifyContent: { xs: 'flex-start', lg: 'center' },
                      alignItems: 'center',
                      minHeight: 56,
                    }}
                  >
                    {verifiedSource && (
                      <BooleanInput source={verifiedSource} label="Verify" />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )
        }}
      </FormDataConsumer>
    </SimpleFormIterator>
  )
}

const ContractMetadataPanel = () => {
  const record = useRecordContext()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const [retryLoading, setRetryLoading] = React.useState(false)
  const [previewLoading, setPreviewLoading] = React.useState(false)
  const metadataFields = React.useMemo(
    () => record?.metadata_fields ?? [],
    [record?.metadata_fields]
  )
  const hasMetadata = metadataFields.length > 0
  const terminalNoDataFailure =
    record?.status === 'extraction_failed' &&
    typeof record?.extraction_error_message === 'string' &&
    (record.extraction_error_message.includes(
      'No usable metadata values extracted from document'
    ) ||
      record.extraction_error_message.includes(
        'Unsupported PDF portfolio format'
      ))

  React.useEffect(() => {
    if (hasMetadata) {
      let needsSorting = false
      let i = 1
      while (i < metadataFields.length) {
        const prevIsMandatory = isFieldMandatory(
          metadataFields[i - 1].field_name
        )
        const currIsMandatory = isFieldMandatory(metadataFields[i].field_name)
        if (!prevIsMandatory && currIsMandatory) {
          needsSorting = true
          break
        }
        i += 1
      }

      if (needsSorting) {
        metadataFields.sort((a, b) => {
          const aIsMandatory = isFieldMandatory(a.field_name)
          const bIsMandatory = isFieldMandatory(b.field_name)
          if (aIsMandatory && !bIsMandatory) return -1
          if (!aIsMandatory && bIsMandatory) return 1
          return 0
        })
      }
    }
  }, [hasMetadata, metadataFields])

  if (!record) return null

  const handleRetryExtraction = () => {
    setRetryLoading(true)
    dataProvider
      .retryExtraction(record.id)
      .then(() => {
        notify('AI extraction retried', { type: 'info' })
        refresh()
      })
      .catch((error) => {
        notify(`Error retrying extraction: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setRetryLoading(false)
      })
  }

  const handlePreview = () => {
    if (!record?.id || previewLoading) return
    setPreviewLoading(true)
    dataProvider
      .getContractPreviewUrl(record.id)
      .then((res) => {
        if (!res?.url) {
          throw new Error('No preview URL returned')
        }
        window.open(res.url, '_blank', 'noopener,noreferrer')
      })
      .catch((error) => {
        notify(`Error opening preview: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setPreviewLoading(false)
      })
  }

  return (
    <Box sx={{ mt: 4, mb: 2, overflow: 'hidden', width: '100%' }}>
      <Typography
        variant="h6"
        sx={{
          borderBottom: '1px solid #e5e7eb',
          pb: 1,
          mb: 2,
          fontWeight: 700,
        }}
      >
        Extracted Metadata
      </Typography>
      {!hasMetadata && (
        <Box
          sx={{
            py: 2,
            px: 2.5,
            mb: 2,
            border: '1px solid #fde68a',
            borderRadius: 1.5,
            backgroundColor: '#fffbeb',
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: '#78350f' }}
          >
            No fields were extracted from this document.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.75 }}>
            {terminalNoDataFailure
              ? 'This file is either an unsupported PDF portfolio or has no usable metadata values. Retrying the same file is unlikely to help. Extract inner documents or save as a standard PDF, then re-upload.'
              : 'Retry AI extraction to repopulate metadata fields before review.'}
          </Typography>
          {terminalNoDataFailure && (
            <Box sx={{ mt: 1.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handlePreview}
                disabled={previewLoading}
              >
                {previewLoading ? 'Opening...' : 'Preview PDF'}
              </Button>
            </Box>
          )}
          {!terminalNoDataFailure && (
            <Box sx={{ mt: 1.5 }}>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={
                  <ArrowPathIcon
                    className={`h-4 w-4 ${retryLoading ? 'animate-spin' : ''}`}
                  />
                }
                onClick={handleRetryExtraction}
                disabled={retryLoading}
              >
                Retry AI Extraction
              </Button>
            </Box>
          )}
        </Box>
      )}
      <ArrayInput
        source="metadata_fields"
        label=""
        sx={{
          '& .MuiFormGroup-root': {
            width: '100%',
            px: 0,
            pt: 1,
          },
          '& .RaSimpleFormIterator-root': { width: '100%' },
          '& .RaSimpleFormIterator-list': { width: '100%' },
          '& .RaSimpleFormIterator-form': { width: '100%' },
          '& .RaSimpleFormIterator-line': {
            border: 'none',
            width: '100%',
            maxWidth: 'none',
            display: 'block',
          },
          '& .RaSimpleFormIterator-index': { display: 'none' },
          '& .RaSimpleFormIterator-action': { display: 'none' },
        }}
      >
        <MetadataIterator />
      </ArrayInput>

      <FormDataConsumer>
        {({ formData }) => {
          if (formData.status !== 'extraction_failed') return null
          return (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: '#fef2f2',
                border: '1px solid #fee2e2',
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" color="error" gutterBottom>
                Extraction Error
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formData.extraction_error_message ? (
                  <span>{formData.extraction_error_message}</span>
                ) : (
                  <span>
                    The AI extraction failed. Common causes are unreadable PDF
                    text, no usable fields extracted, or provider/rate-limit
                    issues. You can retry using the &quot;Retry AI&quot; button
                    in the header.
                  </span>
                )}
              </Typography>
            </Box>
          )
        }}
      </FormDataConsumer>
    </Box>
  )
}

const EditActions = () => (
  <TopToolbar>
    <ListButton />
  </TopToolbar>
)

const ContractEditToolbar = (props) => (
  <Toolbar
    {...props}
    sx={{
      px: 0,
      mt: 3,
      display: 'flex',
      justifyContent: 'flex-start',
      gap: 1.5,
    }}
  >
    <SaveButton />
    <DeleteButton mutationMode="pessimistic" />
  </Toolbar>
)

const ContractStatusInput = () => {
  const record = useRecordContext()
  if (!record) return null

  const editableStatuses = ['review_required', 'completed']
  const isEditable = editableStatuses.includes(record.status)

  if (!isEditable) {
    return (
      <SelectInput
        source="status"
        label="Contract Status"
        fullWidth
        disabled
        sx={{
          mb: 0,
          '& .MuiFormControl-root': { mt: 0, mb: 0, width: '100%' },
          '& .MuiInputBase-root': { height: 56 },
        }}
        choices={[
          { id: 'uploaded', name: 'Uploaded' },
          { id: 'processing', name: 'Processing' },
          { id: 'review_required', name: 'Review Required' },
          { id: 'completed', name: 'Verified' },
          { id: 'failed', name: 'Failed' },
          { id: 'extraction_failed', name: 'Extraction Failed' },
        ]}
      />
    )
  }

  return (
    <SelectInput
      source="status"
      label="Contract Status"
      fullWidth
      sx={{
        mb: 0,
        '& .MuiFormControl-root': { mt: 0, mb: 0, width: '100%' },
        '& .MuiInputBase-root': { height: 56 },
      }}
      choices={[
        { id: 'review_required', name: 'Review Required' },
        { id: 'completed', name: 'Verified' },
      ]}
    />
  )
}

export const ContractEdit = () => (
  <Edit mutationMode="pessimistic" actions={<EditActions />}>
    <Box sx={{ p: { xs: 1.5, md: 3 }, width: '100%' }}>
      <ContractSummaryHeader />
      <SimpleForm
        toolbar={<ContractEditToolbar />}
        sx={{
          '& .RaSimpleForm-form': {
            backgroundColor: '#fff',
            width: '100%',
            maxWidth: 'none',
          },
          '& .RaSimpleForm-form > *': {
            width: '100%',
            maxWidth: 'none',
          },
          '& .RaSimpleFormStack-root': {
            width: '100%',
            maxWidth: 'none',
          },
          '& .MuiInputBase-root': { borderRadius: 1.5 },
        }}
      >
        <ContractStatusPolling />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ReferenceInput
              source="vendor_id"
              reference="contracts/vendors/options"
              label="Linked Vendor"
            >
              <SelectInput optionText="name" fullWidth />
            </ReferenceInput>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput source="contract_type" label="Document Type" fullWidth />
          </Grid>
        </Grid>

        <ContractMetadataPanel />

        <Box
          sx={{
            mt: 6,
            backgroundColor: '#f9fafb',
            p: 4,
            width: '100%',
            borderRadius: 2,
            border: '1px solid #e5e7eb',
          }}
        >
          <Typography
            variant="subtitle2"
            color="textSecondary"
            sx={{ mb: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}
          >
            Administrative Info
          </Typography>
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={6}>
              <ContractStatusInput />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: '#fff',
                  border: '1px solid #e7e5e4',
                  borderRadius: 1,
                  minHeight: '56px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: 'block', mb: 0.2, lineHeight: 1 }}
                >
                  Uploaded On
                </Typography>
                <DateField
                  source="created_at"
                  showTime
                  sx={{ display: 'block', fontSize: '0.9rem', fontWeight: 600 }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </SimpleForm>
    </Box>
  </Edit>
)
